import { z } from "zod";

import { bessConfigSnapshotSchema } from "./schemas/bessConfigSnapshot";

// ---------------------------------------------------------------------------
// LeadIntakeView (Phase 5.41) — Anzeige-DTO fuer Lead-Anfragen
// ---------------------------------------------------------------------------
// `lead_intakes.payload` ist freies jsonb. Aelterer Eintraege koennen Felder
// auslassen oder Werte falscher Form enthalten. Der View-DTO normalisiert die
// Felder, die das Projekt-Detail wirklich rendert — alles als `string | null`,
// damit die UI keine eigenen Schutzwaelle bauen muss. `metadata` bleibt eine
// flach-typisierte Map (string → string|number|boolean|null), gerendert als
// generische Meta-Tags.

const safeString = z
  .union([z.string(), z.number(), z.boolean()])
  .nullable()
  .optional()
  .transform((v) => {
    if (v === null || v === undefined) return null;
    const s = typeof v === "string" ? v : String(v);
    const trimmed = s.trim();
    return trimmed === "" ? null : trimmed;
  });

function isFlatMetadataValue(value: unknown): boolean {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

// LEAD-18: `metadata` FELDWEISE lesen (dieselbe Falle wie der Fix 6.627.0 an
// der Adresse, nur in der Anzeige). Seit RO-3 traegt ein gekaufter Lead
// `metadata.aroundhome` als OBJEKT — der Gesamt-Parse scheiterte daran, und
// `serializeIntakeView` fiel auf einen LEEREN Payload zurueck: kein Name,
// keine Adresse, keine Beschreibung im Projekt-Detail. Verschachtelte Werte
// werden hier weggelassen (die Sicht ist flach; was das Detail aus ihnen
// braucht, hebt der Serializer einzeln, wie `bessConfig`), die flachen
// bleiben — und mit ihnen der Rest des Payloads.
const safeMetadata = z.preprocess(
  (v) =>
    v !== null && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(
          Object.entries(v as Record<string, unknown>).filter(([, value]) =>
            isFlatMetadataValue(value),
          ),
        )
      : v,
  z
    .record(
      z.string(),
      z.union([z.string(), z.number(), z.boolean(), z.null()]),
    )
    .optional()
    .transform((v) => v ?? {}),
);

// Der Service uebergibt `lead_intakes.payload` an dieses Schema. Unerwartete
// Felder werden ignoriert (kein `.strict()`), bekannte Felder werden defensiv
// gecasted. Das Result wandert direkt in die JSON-Response.
export const leadIntakePayloadViewSchema = z.object({
  source: safeString,
  salutation: safeString,
  displayName: safeString,
  firstName: safeString,
  lastName: safeString,
  email: safeString,
  phone: safeString,
  company: safeString,
  street: safeString,
  zip: safeString,
  city: safeString,
  description: safeString,
  topic: safeString,
  sourcePage: safeString,
  locale: safeString,
  metadata: safeMetadata,
});
export type LeadIntakePayloadView = z.infer<typeof leadIntakePayloadViewSchema>;

// LEAD-22 — die ganze Aroundhome-Anfrage, beim Anzeigen aus dem aufbewahrten
// Mailtext gelesen (`shared/inboundMail/aroundhomeAnfrage.ts`). `weitere`
// traegt, was der Leser nicht kennt — wortgetreu; `originalText` ist die
// Mail selbst, damit niemand mehr das Postfach oeffnen muss.
const aroundhomeAngabeViewSchema = z.object({
  label: z.string().nullable(),
  value: z.string(),
  art: z.enum(["text", "phone", "email"]),
});
export const aroundhomeAnfrageViewSchema = z.object({
  kontakt: z.array(aroundhomeAngabeViewSchema),
  angaben: z.array(aroundhomeAngabeViewSchema),
  weitere: z.array(aroundhomeAngabeViewSchema),
  originalText: z.string(),
});
export type AroundhomeAnfrageView = z.infer<typeof aroundhomeAnfrageViewSchema>;

// SOLAR-CHECK-4 — die Anfrage aus dem Solar-Check der Website, in derselben
// Ordnung wie die Aroundhome-Anfrage (`shared/roof/solarCheckAnfrage.ts`).
export const solarCheckAnfrageViewSchema = z.object({
  naechsterHandgriff: z.string().nullable(),
  richtpreisBereit: z.boolean(),
  kontakt: z.array(aroundhomeAngabeViewSchema),
  angaben: z.array(aroundhomeAngabeViewSchema),
  weitere: z.array(aroundhomeAngabeViewSchema),
  nachricht: z.string().nullable(),
});
export type SolarCheckAnfrageView = z.infer<typeof solarCheckAnfrageViewSchema>;

export const leadIntakeViewSchema = z.object({
  id: z.string().uuid(),
  source: z.string(),
  receivedAt: z.string().datetime({ offset: true }),
  payload: leadIntakePayloadViewSchema,
  // Phase 7.4 — BESS-Konfigurator-Lead. `metadata` ist im View flach
  // (string→primitive), der verschachtelte Snapshot wuerde dort verloren
  // gehen. Deshalb hebt der Serializer ihn auf die Top-Ebene + setzt
  // `leadType` aus `metadata.lead_type`. Beide null bei Nicht-BESS-Leads.
  leadType: z.string().nullable().default(null),
  bessConfig: bessConfigSnapshotSchema.nullable().default(null),
  // LEAD-22 — nur bei einem gekauften Aroundhome-Lead mit aufbewahrter Mail.
  aroundhome: aroundhomeAnfrageViewSchema.nullable().default(null),
  // SOLAR-CHECK-4 — nur bei einer Anfrage aus dem Solar-Check der Website.
  solarCheck: solarCheckAnfrageViewSchema.nullable().default(null),
});
export type LeadIntakeView = z.infer<typeof leadIntakeViewSchema>;

// Endpoint-Wrap. Existiert kein Intake fuer das Projekt, antwortet die API mit
// `{ ok: true, data: null }` — der Hook braucht keinen 404-Sonderpfad.
export const leadIntakeViewResponseSchema = z.object({
  ok: z.literal(true),
  data: leadIntakeViewSchema.nullable(),
});
export type LeadIntakeViewResponse = z.infer<
  typeof leadIntakeViewResponseSchema
>;

// Bestimmt aus dem rohen Payload die Description-Preview (max 80 chars + …)
// fuer den /leads-Active-Lead-Listen-Eintrag. Whitespace-Newlines werden in
// Spaces gefaltet, damit die Preview eine Zeile bleibt.
export const LEAD_DESCRIPTION_PREVIEW_LIMIT = 80;

export function buildLeadDescriptionPreview(
  raw: unknown,
): string | null {
  if (raw === null || raw === undefined) return null;
  const s = typeof raw === "string" ? raw : String(raw);
  const collapsed = s.replace(/\s+/g, " ").trim();
  if (collapsed === "") return null;
  if (collapsed.length <= LEAD_DESCRIPTION_PREVIEW_LIMIT) return collapsed;
  // Slice auf 80 Zeichen plus Ellipsis; ein trailing Space vor "…" wird
  // gestripped, damit "Hallo ich …" nicht "Hallo  …" wird.
  const sliced = collapsed.slice(0, LEAD_DESCRIPTION_PREVIEW_LIMIT).trimEnd();
  return `${sliced}…`;
}
